import { ApolloQueryResult } from '@apollo/client';
import {
  ModalBody,
  ModalCloseButton,
  Modal,
  ModalHeader,
  Button,
  useDisclosure,
  Text,
  Flex,
  ModalFooterButtons,
} from '@televet/kibble-ui';
import React from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useClipboard } from 'shared/hooks/useClipboard';
import { GetClinicPhoneNumbersQuery, useTwilioPurchaseNumberMutation } from 'shared/types/graphql';

interface IPurchaseTwilioNumberProps {
  totalNumbers: number;
  refetchPhoneNumbers: () => Promise<ApolloQueryResult<GetClinicPhoneNumbersQuery>>;
}
export const PurchaseTwilioNumber = ({
  refetchPhoneNumbers,
  totalNumbers,
}: IPurchaseTwilioNumberProps): JSX.Element => {
  const { currentClinicId, currentClinic } = useClinicUser();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [purchaseTwilioNumber, { data: purchasedNumber, loading: isPurchasingNumber }] =
    useTwilioPurchaseNumberMutation({
      variables: {
        data: {
          clinicId: currentClinicId,
        },
      },
    });

  const { copy } = useClipboard();
  const purchaseNumber = async (): Promise<void> => {
    await purchaseTwilioNumber();
    await refetchPhoneNumbers();
  };

  const handlePurchaseTwilioNumberClick = (): void => {
    onOpen();
  };
  return (
    <>
      <Button
        size="sm"
        isDisabled={totalNumbers >= 2}
        variant={'secondary'}
        iconName={'plus'}
        onClick={handlePurchaseTwilioNumberClick}
      >
        Purchase Local Twilio Number
      </Button>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalHeader>Purchase a Twilio Number</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text>
            Press &quot;Buy Number&quot; to Purchase a Twilio Number that most closely aligns with the area code{' '}
            <Text variant="success" fontWeight="bold">
              {currentClinic?.postalCode ? ` (${currentClinic.postalCode})` : ''}
            </Text>
            , city{' '}
            <Text variant="success" fontWeight="bold">
              {currentClinic?.city ? ` (${currentClinic.city})` : ''}
            </Text>
            , or state{' '}
            <Text variant="success" fontWeight="bold">
              {currentClinic?.state ? ` (${currentClinic.state})` : ''}
            </Text>{' '}
            of <Text fontWeight="bold">{currentClinic?.name}</Text>
          </Text>
          {purchasedNumber && (
            <Flex pt="1em" alignItems="center" verticalAlign="center">
              <Text fontWeight="bold" alignSelf="center">
                Purchased Number:{' '}
                <Text variant="success">{purchasedNumber.twilioPurchaseNumber?.payload.phoneNumber}</Text>
              </Text>
              <Button
                onClick={(): void => copy(purchasedNumber.twilioPurchaseNumber?.payload.phoneNumber)}
                variant="secondarySubtle"
                iconName="twoSquares"
                size="sm"
                marginLeft="1em"
              >
                Copy
              </Button>
            </Flex>
          )}
        </ModalBody>
        <ModalFooterButtons
          submitText="Buy Number"
          onSubmit={purchaseNumber}
          isDisabled={!!purchasedNumber}
          isLoading={isPurchasingNumber}
          marginBottom={'8px'}
        />
      </Modal>
    </>
  );
};
